import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import {grey} from '@mui/material/colors'
import ProgressDialog from 'app/components/dialogs/ProgressDialog'
// import BasicInfoForm from 'app/pages/profileWizard/components/BasicInfoForm'
import Box from '@mui/material/Box'
import {useOnActionPost} from 'app/hooks/common/useOnActionPost'
import {useAppNavigate} from 'app/hooks/navigation/useAppNavigate'
import React from 'react'
import {Link as RouterLink} from 'react-router-dom'
import toastr from 'toastr'
import BasicInfoForm from './BasicInfoForm'
import EmailSent from './EmailSent'
import {SignupProviderSteps, TOTAL_STEPS, useSignupProvider} from './SignUpContextProvider'

export default function Signup() {
  const createUser = useOnActionPost()
  const {setModalOpen, step, goToNextStep, goToPrevStep, modalOpen, starterInfo, setStarterData} =
    useSignupProvider()
  const submitButtonRef = React.useRef<HTMLButtonElement>(null)
  const navigate = useAppNavigate()

  const close = () => {
    navigate('/')
  }

  async function handleSubmit(data: any) {
    const si = {
      email: data.email,
      last_name: data.lastName ?? '',
      first_name: data.firstName ?? '',
    }
    setStarterData(si)
    //create the user
    const result: any = await createUser.mutateAsync({
      route: 'std/user',
      data: si,
    })
    if (result?.email) {
      toastr.success('Account Created Successfully')
      goToNextStep()
    } else {
      toastr.error('An error occurred creating the user account.')
    }
  }

  const content:
    | {node: React.ReactNode; title: string | React.ReactNode; subTitle: string | React.ReactNode}
    | undefined = React.useMemo(() => {
    if (step === SignupProviderSteps.Intro) {
      return {
        node: (
          <>
            <DialogContent></DialogContent>
            <DialogActions sx={{display: 'flex', bgcolor: grey[200]}}>
              <Button to='/' component={RouterLink} variant='text'>
                Cancel
              </Button>
              <Button
                disabled={false}
                onClick={goToNextStep}
                variant='contained'
                color='primary'
                size='large'
              >
                Continue
              </Button>
            </DialogActions>
          </>
        ),
        title: <DialogTitle fontWeight={700}>Sign up for iScout</DialogTitle>,
        subTitle: (
          <Typography variant={'h6'} m={3} mb={1} fontWeight={700} color={grey[800]}>
            iScout is free to use for businesses and individuals wanting to keep their profiles
            current. A subscription is required for all other functionality including projects and
            AI-powered entity searches for innovations, opportunities, businesses, awards, people
            and more.
          </Typography>
        ),
      }
    }
    if (step === SignupProviderSteps.EnterBasicInfo) {
      return {
        node: (
          <>
            <DialogContent>
              <BasicInfoForm
                loading={false}
                submitButtonProps={{style: {display: 'none'}}}
                submitButtonRef={submitButtonRef}
                goToNextStep={goToNextStep}
                initialValues={starterInfo}
                onSubmit={handleSubmit}
              />
            </DialogContent>
            <DialogActions
              sx={{display: 'flex', justifyContent: 'space-between', bgcolor: grey[200]}}
            >
              <Box>
                <Button onClick={goToPrevStep}>
                  <ArrowBackIcon sx={{mr: 1}} />
                  Go Back
                </Button>
              </Box>
              <Box>
                <Button to='/' component={RouterLink} variant='text'>
                  Cancel
                </Button>

                <Button
                  disabled={false}
                  onClick={() => submitButtonRef.current?.click()}
                  variant='contained'
                  color='primary'
                  size='large'
                >
                  Sign Up
                </Button>
              </Box>
            </DialogActions>
          </>
        ),
        title: <DialogTitle fontWeight={700}>Sign Up</DialogTitle>,
        subTitle: (
          <Typography variant={'h6'} m={3} mb={1} fontWeight={500} color={grey[600]}>
            Provide the following information to Sign up
          </Typography>
        ),
      }
    }
    if (step === SignupProviderSteps.EmailSent) {
      return {
        node: <EmailSent onModalClose={close} />,
        title: <DialogTitle fontWeight={700}>Email Sent</DialogTitle>,
        subTitle: (
          <Box fontWeight={500} color={grey[600]} textAlign={'center'} p={2}>
            <Typography variant={'h6'}>
              Your account is being created, please check your email for confirmation
            </Typography>
            <Box component={'span'} color={grey[900]} fontWeight={700} fontSize={'1.4em'}>
              {starterInfo.email}
            </Box>
            <Typography variant={'h6'} pt={4}>
              Follow the instructions provided to login to iScout.
            </Typography>
          </Box>
        ),
      }
    }
  }, [step, goToNextStep])
  return (
    <ProgressDialog
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      totalSteps={TOTAL_STEPS}
      step={step}
      maxWidth='md'
      title={content?.title}
      subTitle={content?.subTitle}
    >
      {content?.node}
    </ProgressDialog>
  )
}
